import * as Pkce from "./pkce";
import * as QueryString from "./querystring";
import * as Config from "configuration";
import * as Authorization from "./authorization";
import * as ServiceWorkerOAuth from "service-worker/es/oauth";
import * as Broker from "message-broker";
const redirectUri = () => {
    if (Broker.isNativeApp() && Broker.checkMinAppVersion("2.0.8")) {
        const config = Config.current();
        return config.oauth2RedirectUri;
    }
    else {
        return window.location.origin + "/";
    }
};
export const exchangeCodeForAccessToken = async (code, code_verifier) => {
    const config = Config.current();
    const url = `${config.oauth2Api}/connect/token`;
    const response = await fetch(url, {
        method: "POST",
        headers: {
            Accept: "application/json; charset=utf-8",
            "Content-Type": "application/x-www-form-urlencoded",
        },
        body: QueryString.stringify({
            grant_type: "authorization_code",
            client_id: config.oauth2ClientId,
            redirect_uri: redirectUri(),
            code,
            code_verifier,
        }),
    });
    if (response.status !== 200) {
        const text = await response.text();
        throw new Error(text);
    }
    return response.json();
};
export const exchangeRefreshForAccessToken = async (token, init) => {
    const config = Config.current();
    const url = `${config.oauth2Api}/connect/token`;
    const response = await fetch(url, {
        method: "POST",
        headers: {
            Accept: "application/json; charset=utf-8",
            "Content-Type": "application/x-www-form-urlencoded",
            ...init?.headers,
        },
        body: QueryString.stringify({
            grant_type: "refresh_token",
            client_id: config.oauth2ClientId,
            refresh_token: token.refresh_token,
        }),
    });
    if (response.status !== 200) {
        return undefined;
    }
    return response.json();
};
const decodeAccessToken = (accessToken) => {
    const withoutPrefix = accessToken.startsWith("Bearer ")
        ? accessToken.slice(7)
        : accessToken;
    const base64Url = withoutPrefix.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(atob(base64)
        .split("")
        .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
    })
        .join(""));
    return JSON.parse(jsonPayload);
};
export const isCRAccessToken = (token) => {
    const decoded = decodeAccessToken(token);
    return (decoded.client_id === Config.get("oauth2ClientId") &&
        decoded.iss === Config.get("oauth2Api"));
};
const refreshAuthFunction = async (tracked = false, init) => {
    const token = Authorization.tokenFromStorage();
    const refreshed = token
        ? await exchangeRefreshForAccessToken(token, init)
        : undefined;
    if (refreshed) {
        const refreshedAccessToken = Authorization.constructAccessToken(refreshed);
        const refreshResponse = await Authorization.fetchAuthorization(refreshedAccessToken, tracked, init);
        if (refreshResponse.ok) {
            const user = (await refreshResponse.json());
            localStorage.setItem("authorization_token", JSON.stringify(refreshed));
            localStorage.setItem("authorization_user", JSON.stringify(user));
            const authSW = {
                token: Authorization.constructAccessToken(refreshed),
                user,
            };
            await ServiceWorkerOAuth.sendAuth(authSW);
            return { token: refreshed, user };
        }
    }
    // Token présent à l'installation de l'application v2.0.9 + ?
    //
    // if (Broker.isNativeApp()) {
    //   var base64Url = token.access_token.split(".")[1];
    //   var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    //   var jsonPayload = decodeURIComponent(
    //     window
    //       .atob(base64)
    //       .split("")
    //       .map(function (c) {
    //         return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
    //       })
    //       .join("")
    //   );
    //   alert(
    //     "Déconnexion : fetchAuthenticated 2 : url : " +
    //       input +
    //       "  #### payload :" +
    //       jsonPayload
    //   );
    // }
    Authorization.deconnect();
    await ServiceWorkerOAuth.sendAuth(undefined);
    throw new Error("refreshAccessToken: Disconnected");
};
let runningRefreshAuth;
const queueRefreshAuth = [];
export const refreshAuth = (tracked = false, init) => {
    if (runningRefreshAuth &&
        (runningRefreshAuth?.tracked === tracked || !tracked)) {
        return runningRefreshAuth.promise;
    }
    else if (runningRefreshAuth) {
        return new Promise((resolve, reject) => {
            queueRefreshAuth.push({
                tracked,
                init,
                resolve,
                reject,
            });
        });
    }
    else {
        return startRefreshAuth(tracked, init);
    }
};
const startRefreshAuth = (tracked, init) => {
    const promise = refreshAuthFunction(tracked, init);
    runningRefreshAuth = {
        tracked,
        init,
        promise,
    };
    promise.then((value) => finishRefreshAuth({ value }), (reason) => finishRefreshAuth(undefined, { reason }));
    return promise;
};
const finishRefreshAuth = (onfulfilledValue, onrejectedReason) => {
    const queuedSameTracked = queueRefreshAuth.filter((item) => item.tracked === runningRefreshAuth?.tracked);
    queuedSameTracked.forEach((item) => {
        if (onfulfilledValue) {
            item.resolve(onfulfilledValue.value);
        }
        else if (onrejectedReason) {
            item.reject(onrejectedReason.reason);
        }
        if (onfulfilledValue || onrejectedReason) {
            queueRefreshAuth.splice(queueRefreshAuth.indexOf(item), 1);
        }
    });
    runningRefreshAuth = undefined;
    const next = queueRefreshAuth.shift();
    if (next) {
        const promise = startRefreshAuth(next.tracked, next.init);
        promise.then(next.resolve, next.reject);
    }
};
export const signIn = () => {
    const config = Config.current();
    const state = Pkce.generateRandomString();
    const code_verifier = Pkce.generateRandomString();
    Pkce.challengeFromVerifier(code_verifier).then((code_challenge) => {
        const params = QueryString.stringify({
            grant_type: "authorization_code",
            response_type: "code",
            scope: config.oauth2Scope,
            client_id: config.oauth2ClientId,
            redirect_uri: redirectUri(),
            state,
            code_challenge,
            code_challenge_method: "S256",
        });
        sessionStorage.setItem("pkce_code_verifier", code_verifier);
        sessionStorage.setItem("pkce_state", state);
        const url = `${config.oauth2Api}/connect/authorize`;
        if (Broker.isNativeApp() && Broker.checkMinAppVersion("2.0.8")) {
            Broker.sendToNative({
                type: "connexion",
                payload: `${url}?${params}`,
            });
        }
        else {
            window.location.href = `${url}?${params}`;
        }
    });
};
export const signOut = (auth) => {
    const config = Config.current();
    const idToken = auth?.id_token;
    if (!idToken) {
        setTimeout(() => {
            window.location.href = "/";
        }, 100);
        return;
    }
    const params = QueryString.stringify({
        post_logout_redirect_uri: redirectUri(),
        id_token_hint: idToken,
    });
    Authorization.clear();
    const url = `${config.oauth2Api}/connect/endsession`;
    setTimeout(() => {
        if (Broker.isNativeApp() && Broker.checkMinAppVersion("2.0.8")) {
            Broker.sendToNative({
                type: "connexion",
                payload: `${url}?${params}`,
            });
        }
        else {
            window.location.href = `${url}?${params}`;
        }
    }, 100);
    return;
};
